import { computed, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { formatCurrency, getNumberFormatter } from '@/lib/number-helpers';
import { useAssetsPerformanceQuery } from '@/queries/assets/use-assets-performance-query';
import { useUserBuyOrdersQuery } from '@/queries/users/use-user-buy-orders';
import { useUserMeQuery } from '@/queries/users/use-users-me-query';
import { SellDialogState } from '@/types/invest/sell-dialog-state-enum.ts';
import { WithdrawalMethod } from '@/types/invest/withdrawal-method-enum';

const isOpen: Ref<boolean> = ref(false);
const currentState: Ref<SellDialogState> = ref(SellDialogState.Creation);
const sellForm: Ref<{
  withdrawalMethod: WithdrawalMethod;
  selectedOrders: Record<string, { selectedAmount: number }>;
}> = ref({
  withdrawalMethod: WithdrawalMethod.Wallet,
  selectedOrders: {},
});

// eslint-disable-next-line max-lines-per-function
export function useSellDialog() {
  const { t: $t } = useI18n();

  const { data: user } = useUserMeQuery();
  const { data: assetData } = useAssetsPerformanceQuery(undefined, 'EUA_DAILY');

  const userId = computed(() => {
    if (!user?.value) return null;

    return user.value.id;
  });

  const { data: buyOrders } = useUserBuyOrdersQuery(userId);

  const lockedOrders = computed(() => {
    return buyOrders.value?.filter((order) => order.isLocked) ?? [];
  });

  const informations = computed(() => {
    const { amountBond, amountAsset, custodyFeesDue } = Object.entries(
      sellForm.value.selectedOrders,
    ).reduce(
      (acc, [orderId, form]) => {
        const relatedOrder = buyOrders.value?.find((o) => o.id === orderId);

        if (!relatedOrder) return acc;

        return {
          amountBond: acc.amountBond + form.selectedAmount,
          amountAsset: acc.amountAsset + form.selectedAmount * (relatedOrder.assetBondRatio ?? 1),
          custodyFeesDue: form.selectedAmount
            ? acc.custodyFeesDue +
              (relatedOrder.custodyFeesDue ?? 0) *
                (form.selectedAmount / relatedOrder.availableBondsToSell)
            : 0,
        };
      },
      { amountBond: 0, amountAsset: 0, custodyFeesDue: 0 },
    );

    return {
      amountBond,
      amountAsset,
      equivalentCO2: amountAsset,
      value: amountAsset * (assetData.value?.stats.currentPrice ?? 0),
      exitFees: 0,
      custodyFeesDue,
    };
  });

  const formatting = {
    amountBond: (value: number) => `${$t('features.sell.formatting.amountBond', { count: value })}`,
    amountAsset: (value: number) => `${getNumberFormatter(2).format(value)} EUAs`,
    equivalentCO2: (value: number) =>
      `${getNumberFormatter(2).format(value)} ${$t('features.sell.formatting.equivalentCO2')}`,
    exitFees: (value: number) => formatCurrency(value, 'EUR'),
    value: (value: number) => formatCurrency(value, 'EUR'),
    custodyFeesDue: (value: number) => formatCurrency(value, 'EUR'),
  };

  const changeModalState = (state: SellDialogState) => {
    currentState.value = state;
  };

  const openDialog = (state: SellDialogState) => {
    isOpen.value = true;
    currentState.value = state;
  };

  const closeDialog = () => {
    isOpen.value = false;
  };

  const formattedInformation = (key: string, value: any): any => {
    if (!(key in formatting)) return value;

    return formatting[key as keyof typeof formatting](value);
  };

  return {
    openDialog,
    closeDialog,
    changeModalState,
    isOpen,
    currentState,
    buyOrders,
    sellForm,
    informations,
    formattedInformation,
    lockedOrders,
  };
}
